import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

export type CarouselDotVariant = 'white' | 'black';

const DOT_SIZE = '0.5rem';
const DOT_GAP = '0.25rem';

export interface CarouselDotPaginationProps {
  scrollSnaps: number[];
  selectedIndex: number;
  onDotClick: (index: number) => void;
  variant?: CarouselDotVariant;
  sx?: SxProps<Theme>;
}

const getDotSx = (isActive: boolean, variant: CarouselDotVariant): SxProps<Theme> => {
  const colour = variant === 'white' ? 'white' : 'black';
  return {
    appearance: 'none',
    WebkitAppearance: 'none',
    width: DOT_SIZE,
    height: DOT_SIZE,
    minWidth: 0,
    borderRadius: '50%',
    border: `1px solid ${colour}`,
    background: isActive ? colour : 'transparent',
    opacity: 1,
    cursor: 'pointer',
    padding: 0,
    transition: 'background-color 0.2s ease',
    '&:hover': {
      background: isActive ? colour : 'transparent',
    },
  };
};

export const CarouselDotPagination = ({
  scrollSnaps,
  selectedIndex,
  onDotClick,
  variant = 'white',
  sx,
}: CarouselDotPaginationProps) => (
  <Box
    role="tablist"
    aria-label="Carousel pagination"
    sx={{
      display: 'flex',
      justifyContent: 'center',
      gap: DOT_GAP,
      ...sx,
    }}
  >
    {scrollSnaps.map((_, index) => (
      <Box
        key={index}
        component="button"
        type="button"
        role="tab"
        aria-selected={index === selectedIndex}
        aria-label={`Go to slide ${index + 1}`}
        onClick={() => onDotClick(index)}
        sx={getDotSx(index === selectedIndex, variant)}
      />
    ))}
  </Box>
);
